import { Badge, BorderBeam, Button, Dropdown, Popover, Progress, Tooltip } from 'antd';
import { lazy, Suspense, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import clsx from 'clsx';
import { Link } from 'react-router-dom';
import { FilesTableEnum } from '../../../enums/FilesTableEnum';
import { useHeader } from '../../../hooks/useHeader';
import { useWebSocketConnectionStatus } from '../../../hooks/useWebSocketConnectionStatus';
import { appendAiGrantPreferencesToUrl } from '../../../lib/aiGrantPreferencesUrl';
import { useAppStore } from '../../../store/store';
import { getAvatarColor } from '../../../utils/avatarColors';
import { getContrastColorForBackground, getUserRoleIconName } from '../../../utils/utils';
import {
  webSocketConnectionLabelKey,
  webSocketConnectionToneClass,
} from '../../../utils/webSocketConnectionStatus';
import BatchesList from '../../Batches/BatchesList';
import GlobalBanner from '../../Common/GlobalBanner';
import GlobalSearchField from '../../Common/GlobalSearchField';
import NotificationsList from '../../Common/NotificationsList';
import { OnBehalfOfDrawer } from '../../Common/OnBehalfOfDrawer';
import PendingGrantsList from '../../Common/PendingGrantsList';
import QuickShareFilesList from '../../Common/QuickShareFilesList';
import { Icon } from '../Icon';

const AppDebugDrawer = lazy(() => import('../../Common/AppDebugDrawer'));

/**
 * Header component to render the top navigation bar for the application.
 *
 * This method manages multiple features such as branding logo, global search field,
 * quick share upload status, and user interaction elements like logout and navigation buttons.
 * The visibility and behavior of different parts of the header adapt dynamically based on
 * user authentication status, current route, and application state.
 *
 * @return {JSX.Element} The rendered header component for the application's top navigation bar.
 */
export default function Header() {
  const { t } = useTranslation();
  const {
    // Auth
    isLoggedIn,
    // User data
    authenticatedUser,
    // On behalf of
    isOnBehalfOfDrawerOpen,
    setIsOnBehalfOfDrawerOpen,
    isAppDebugDrawerOpen,
    setIsAppDebugDrawerOpen,
    // Location
    navigate,
    // Branding
    brandingData,
    isLoadingBrandingData,
    logoSrc,
    // File upload
    uploadQuickShareGroupedFilesQueueWithoutRootFolder,
    isQuickShareUploadFilesCompletedWithoutErrors,
    quickShareGlobalProgressByBytes,
    // Quick share
    // Pending grants
    pendingGrants,
    // Notifications
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    clearAll,
    // Batches
    activeBatches,
    activeCount,
    refreshBatches,
    // Format
    formatTimestamp,
    // File operations
    openFileSafely,
    redirectFileToLocation,
    openAiSummary,
    // Computed values
    isSearchFieldVisible,
    quickShareTitle,
    isInQuickShareRoute,
    isInLoginRoute,
    isInPublicLinkRoute,
    isInViewerWithPublicToken,
    isExternalOrReader,
    menuItems,
    quickShareStatusIcon,
    getNotificationIcon,
    headerIconColor,
    isDevEnvironment,
  } = useHeader();

  const webSocketStatus = useWebSocketConnectionStatus();
  const connectionStatusLabel = t(webSocketConnectionLabelKey(webSocketStatus));
  // Terminal QS status (Completed / Error / …) → badge only; in-progress → Progress only
  const isQuickShareTerminal = Boolean(quickShareStatusIcon);

  const headerStackRef = useRef<HTMLDivElement>(null);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);

  // ponytail: shared icon-control chrome — contrast via headerIconColor, hover wash instead of solid borders
  const headerIconBtnClass =
    'size-10! min-w-0! inline-flex! items-center! justify-center! border-0! p-1.5! shadow-none! rounded-md! transition-colors hover:bg-[color-mix(in_srgb,var(--header-icon-color)_14%,transparent)]!';
  const headerIconWashStyle = {
    color: headerIconColor,
    ['--header-icon-color' as string]: headerIconColor,
  };

  /**
   * Keeps main/sidebar/right-panel top offsets in sync with the fixed header + optional banner height.
   */
  useLayoutEffect(() => {
    const root = document.documentElement;
    const el = headerStackRef.current;
    if (!el) {
      return;
    }

    const syncHeight = (): void => {
      root.style.setProperty('--app-header-stack-height', `${el.getBoundingClientRect().height}px`);
    };

    syncHeight();
    const ro = new ResizeObserver(syncHeight);
    ro.observe(el);

    return () => {
      ro.disconnect();
      root.style.removeProperty('--app-header-stack-height');
    };
  }, []);

  // dashboard-no-print on shell: print CSS hid <header> only; outer div.w-screen still inflated page width.
  return (
    <div
      ref={headerStackRef}
      className="dashboard-no-print fixed top-0 right-0 left-0 z-50 w-screen"
    >
      <header
        className={clsx(
          'h-14 w-full',
          isLoadingBrandingData ? 'bg-gray-medium animate-pulse' : 'bg-blue-dark',
        )}
        style={{ backgroundColor: brandingData?.topBarColorBackground }}
      >
        <div className="flex h-full items-center justify-between px-4">
          <div className="flex flex-row items-center gap-6">
            <Link
              to={
                isInPublicLinkRoute || isInViewerWithPublicToken
                  ? '/'
                  : isInLoginRoute
                    ? '/'
                    : `/files/${FilesTableEnum.Personal}`
              }
            >
              <div className="lg:w-48">
                <img
                  src={logoSrc}
                  alt={brandingData?.companyName || 'Filegrant Enterprise'}
                  className={clsx(
                    'h-8 transition-all duration-300',
                    isLoadingBrandingData && 'opacity-25 blur-lg',
                  )}
                />
              </div>
            </Link>
            {!isInPublicLinkRoute && !isInViewerWithPublicToken && isSearchFieldVisible && (
              <div className="hidden sm:block">
                <GlobalSearchField />
              </div>
            )}
          </div>
          {isInPublicLinkRoute || isInViewerWithPublicToken ? (
            <>
              <span
                className="flex flex-1 justify-center text-center text-base font-medium"
                style={{ color: headerIconColor }}
              >
                {t('common.publicLinkTitle')}
              </span>
              <div className="lg:w-48" aria-hidden />
            </>
          ) : null}
          {!isInPublicLinkRoute && !isInViewerWithPublicToken && isLoggedIn && (
            <div className="flex flex-row items-center gap-2.5">
              {authenticatedUser?.aiGrantURL && (
                <BorderBeam color={headerIconColor} duration={3} size={56} lineWidth={1.5}>
                  <Button
                    type="default"
                    shape="round"
                    className="header-aigrant-btn relative font-semibold"
                    icon={
                      <Icon
                        name="BrainCircuitIcon"
                        className="h-4 w-4"
                        style={{ color: headerIconColor }}
                      />
                    }
                    style={{
                      color: headerIconColor,
                      borderColor: `color-mix(in srgb, ${headerIconColor} 35%, transparent)`,
                      background: `color-mix(in srgb, ${headerIconColor} 10%, transparent)`,
                    }}
                    onClick={() => {
                      const {
                        locale,
                        colorSchemePreference,
                        highContrastEnabled,
                        reduceMotionEnabled,
                      } = useAppStore.getState();
                      window.open(
                        appendAiGrantPreferencesToUrl(authenticatedUser.aiGrantURL!, {
                          locale,
                          colorSchemePreference,
                          highContrastEnabled,
                          reduceMotionEnabled,
                        }),
                        '_blank',
                        'noopener,noreferrer',
                      );
                    }}
                  >
                    {t('common.AIGrant')}
                  </Button>
                </BorderBeam>
              )}

              {/* ponytail: Drive-like icon strip (gap-2); outer gap-2.5 keeps avatar a beat farther out */}
              <div className="flex flex-row items-center gap-2">
                {/* Cluster order (→ user): mobile search → Quick Share → file share requests → batches → notifications */}
                {!isInPublicLinkRoute && !isInViewerWithPublicToken && isSearchFieldVisible && (
                  <div className="sm:hidden">
                    <Popover
                      placement="bottomRight"
                      trigger="click"
                      open={isMobileSearchOpen}
                      onOpenChange={setIsMobileSearchOpen}
                      content={
                        <div className="w-[min(calc(100vw-2rem),360px)]">
                          <GlobalSearchField
                            className="w-full [&_.ant-select-selector]:rounded-full!"
                            onAfterSelect={() => setIsMobileSearchOpen(false)}
                          />
                        </div>
                      }
                    >
                      <Tooltip title={t('a11y.globalSearch')}>
                        <Button
                          type="text"
                          aria-label={t('a11y.globalSearch')}
                          className={headerIconBtnClass}
                          style={headerIconWashStyle}
                        >
                          <Icon name="SearchIcon" className="h-7 w-7 shrink-0" />
                        </Button>
                      </Tooltip>
                    </Popover>
                  </div>
                )}
                {!isExternalOrReader && authenticatedUser?.isQSAvailable !== false && (
                  <>
                    {uploadQuickShareGroupedFilesQueueWithoutRootFolder.length > 0 ? (
                      <Popover
                        placement="bottomRight"
                        content={
                          <div className="bg-white p-0">
                            <div className="border-gray-medium flex items-center gap-2 border-b px-3 py-2">
                              {quickShareStatusIcon ? (
                                <Icon
                                  name={quickShareStatusIcon as any}
                                  className={clsx('h-4 w-4 shrink-0', {
                                    'text-red-dark':
                                      quickShareStatusIcon === 'UnplugIcon' ||
                                      quickShareStatusIcon === 'ServerCrashIcon',
                                    'text-yellow-dark':
                                      quickShareStatusIcon === 'TriangleAlertIcon',
                                    'text-green': quickShareStatusIcon === 'CircleCheckIcon',
                                  })}
                                />
                              ) : null}
                              <span className="text-blue-dark min-w-0 flex-1 truncate text-sm font-medium">
                                {quickShareTitle}
                              </span>
                              <span className="text-gray-dark shrink-0 text-sm tabular-nums">
                                {quickShareGlobalProgressByBytes}%
                              </span>
                            </div>
                            <QuickShareFilesList
                              files={uploadQuickShareGroupedFilesQueueWithoutRootFolder}
                              handleRemoveFile={() => {}}
                            />
                            {isQuickShareUploadFilesCompletedWithoutErrors &&
                              !isInQuickShareRoute && (
                                <div className="text-gray-dark z-10 flex w-full justify-center pb-2 underline">
                                  <Link to="/quick-share">
                                    {t('common.startNewUploadAndShare')}
                                  </Link>
                                </div>
                              )}
                          </div>
                        }
                      >
                        <Tooltip
                          title={t('a11y.quickShare', {
                            percent: quickShareGlobalProgressByBytes,
                          })}
                        >
                          {/* ponytail: progress XOR terminal badge — never both (100% + green check was confusing) */}
                          <Badge
                            count={
                              isQuickShareTerminal ? (
                                <Icon
                                  name={quickShareStatusIcon as any}
                                  className={clsx('h-3.5 w-3.5', {
                                    'text-red-dark':
                                      quickShareStatusIcon === 'UnplugIcon' ||
                                      quickShareStatusIcon === 'ServerCrashIcon',
                                    'text-yellow-dark':
                                      quickShareStatusIcon === 'TriangleAlertIcon',
                                    'text-green': quickShareStatusIcon === 'CircleCheckIcon',
                                  })}
                                />
                              ) : (
                                0
                              )
                            }
                            size="small"
                            className="z-10!"
                            offset={[-2, 2]}
                          >
                            <Button
                              onClick={() => navigate('/quick-share')}
                              type="text"
                              aria-label={t('a11y.quickShare', {
                                percent: quickShareGlobalProgressByBytes,
                              })}
                              className={clsx(
                                headerIconBtnClass,
                                !isQuickShareTerminal && 'w-auto! min-w-10! gap-1!',
                              )}
                              style={headerIconWashStyle}
                            >
                              {/* ponytail: rabbit glyph fills the 24 viewBox denser than Bell — scale keeps optical size matched */}
                              <Icon name="RabbitIcon" className="h-7 w-7 shrink-0 scale-90" />
                              {!isQuickShareTerminal ? (
                                <Progress
                                  type="circle"
                                  percent={quickShareGlobalProgressByBytes}
                                  size={18}
                                  strokeWidth={10}
                                  showInfo={false}
                                  strokeColor={headerIconColor}
                                  trailColor={`color-mix(in srgb, ${headerIconColor} 20%, transparent)`}
                                  className="leading-none!"
                                />
                              ) : null}
                            </Button>
                          </Badge>
                        </Tooltip>
                      </Popover>
                    ) : (
                      <Tooltip title={t('common.goToQuickShare')}>
                        <Button
                          type="text"
                          aria-label={t('common.goToQuickShare')}
                          className={headerIconBtnClass}
                          style={headerIconWashStyle}
                          onClick={() => navigate('/quick-share')}
                        >
                          {/* ponytail: rabbit glyph fills the 24 viewBox denser than Bell — scale keeps optical size matched */}
                          <Icon name="RabbitIcon" className="h-7 w-7 shrink-0 scale-90" />
                        </Button>
                      </Tooltip>
                    )}
                  </>
                )}

                {authenticatedUser?.isPartOfTeamSA === true && (
                  <Popover
                    placement="bottomRight"
                    content={
                      <div className="bg-white p-0">
                        <PendingGrantsList
                          grants={pendingGrants}
                          formatTimestamp={formatTimestamp}
                          onGrantClick={(grant) => navigate(`/file-share-requests/${grant.id}`)}
                        />
                        <div className="text-gray-dark z-10 flex w-full justify-center pb-2 underline">
                          <Link to="/file-share-requests">{t('common.viewAll')}</Link>
                        </div>
                      </div>
                    }
                    trigger="click"
                  >
                    <Tooltip title={t('common.fileShareRequests')}>
                      <Badge
                        count={pendingGrants.length}
                        size="small"
                        className="z-10!"
                        offset={[-3, 2]}
                      >
                        <Button
                          type="text"
                          aria-label={
                            pendingGrants.length > 0
                              ? t('a11y.pendingGrants', { count: pendingGrants.length })
                              : t('a11y.pendingGrantsButton')
                          }
                          className={headerIconBtnClass}
                          style={headerIconWashStyle}
                        >
                          <Icon name="ClipboardClockIcon" className="h-7 w-7 shrink-0" />
                        </Button>
                      </Badge>
                    </Tooltip>
                  </Popover>
                )}

                {activeBatches.length > 0 && (
                  <Popover
                    placement="bottomRight"
                    content={<BatchesList batches={activeBatches} onRefresh={refreshBatches} />}
                    trigger="click"
                  >
                    <Badge
                      count={activeBatches.length}
                      size="small"
                      className="z-10!"
                      offset={[-3, 2]}
                    >
                      <Button
                        type="text"
                        aria-label={t('a11y.activeBatches', { count: activeBatches.length })}
                        className={headerIconBtnClass}
                        style={headerIconWashStyle}
                      >
                        <Icon
                          name={activeCount > 0 ? 'LoaderIcon' : 'CircleXIcon'}
                          className={clsx('h-7 w-7 shrink-0', {
                            'animate-spin': activeCount > 0,
                            'animate-pulse': activeCount === 0,
                          })}
                        />
                      </Button>
                    </Badge>
                  </Popover>
                )}

                {notifications.length > 0 && (
                  <Popover
                    placement="bottomRight"
                    content={
                      <NotificationsList
                        notifications={notifications}
                        unreadCount={unreadCount}
                        markAsRead={markAsRead}
                        markAllAsRead={markAllAsRead}
                        clearAll={clearAll}
                        formatTimestamp={formatTimestamp}
                        getNotificationIcon={getNotificationIcon}
                        openFileSafely={openFileSafely}
                        redirectFileToLocation={redirectFileToLocation}
                        openAiSummary={openAiSummary}
                      />
                    }
                    trigger="click"
                  >
                    <Badge count={unreadCount} size="small" className="z-10!" offset={[-3, 2]}>
                      <Button
                        type="text"
                        aria-label={
                          unreadCount > 0
                            ? t('a11y.notificationsCount', { count: unreadCount })
                            : t('a11y.notificationsButton')
                        }
                        className={headerIconBtnClass}
                        style={headerIconWashStyle}
                      >
                        <Icon name="BellIcon" className="h-7 w-7 shrink-0" />
                      </Button>
                    </Badge>
                  </Popover>
                )}
              </div>

              <Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomRight">
                <Button
                  type="text"
                  aria-label={t('a11y.userMenu')}
                  className="min-w-0! rounded-md! border-0! p-2! shadow-none! transition-colors hover:bg-[color-mix(in_srgb,var(--header-icon-color)_14%,transparent)]!"
                  style={headerIconWashStyle}
                >
                  <div className="flex flex-row items-center gap-2">
                    {/* ponytail: status dot overlays avatar top-right (not the outer pill) */}
                    <span className="relative inline-flex shrink-0">
                      <div
                        className="flex h-7 w-7 items-center justify-center rounded-full uppercase"
                        style={{
                          backgroundColor: getAvatarColor(authenticatedUser?.id || 0),
                          color: getContrastColorForBackground(
                            getAvatarColor(authenticatedUser?.id || 0),
                          ),
                        }}
                      >
                        {authenticatedUser?.firstName?.[0] ||
                          authenticatedUser?.lastName?.[0] ||
                          authenticatedUser?.userName?.[0] ||
                          authenticatedUser?.email?.[0] ||
                          'N/D'}
                      </div>
                      <span
                        className={clsx(
                          'ring-blue-dark absolute -top-0.5 -right-0.5 size-2 rounded-full ring-2',
                          webSocketConnectionToneClass(webSocketStatus),
                        )}
                        title={connectionStatusLabel}
                        aria-label={connectionStatusLabel}
                        role="status"
                        data-testid="websocket-connection-dot"
                        data-state={webSocketStatus}
                      />
                    </span>
                    <div className="flex flex-col items-start">
                      <div className="flex flex-row items-center gap-1">
                        {authenticatedUser?.role && (
                          <Icon
                            name={getUserRoleIconName(authenticatedUser?.role)}
                            className="h-4 w-4"
                            style={{ color: headerIconColor }}
                          />
                        )}
                        <Tooltip title={authenticatedUser?.email || authenticatedUser?.userName}>
                          <span className="text-sm" style={{ color: headerIconColor }}>
                            {authenticatedUser?.firstName || authenticatedUser?.lastName ? (
                              <>
                                {authenticatedUser?.firstName} {authenticatedUser?.lastName}
                              </>
                            ) : (
                              <>{authenticatedUser?.email || authenticatedUser?.userName}</>
                            )}
                          </span>
                        </Tooltip>
                      </div>
                    </div>
                  </div>
                </Button>
              </Dropdown>
            </div>
          )}
        </div>
      </header>

      <GlobalBanner />

      <OnBehalfOfDrawer
        open={isOnBehalfOfDrawerOpen}
        onClose={() => setIsOnBehalfOfDrawerOpen(false)}
      />
      {isDevEnvironment && (
        <Suspense fallback={null}>
          <AppDebugDrawer
            open={isAppDebugDrawerOpen}
            onClose={() => setIsAppDebugDrawerOpen(false)}
          />
        </Suspense>
      )}
    </div>
  );
}
